import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import symbolsAPI from '../api/symbolsAPI';
import { useTranslation } from 'react-i18next';

interface SymbolsContextType {
	symbolCategories: SymbolCategoryDTO[];
	allSymbolsData: SymbolCategoryDTO[];
	selectedCategoryData: SymbolCategoryDTO | null;
	isLoadingCategories: boolean;
	isLoadingSymbols: boolean;
	isLoadingAllSymbols: boolean;
	error: string | null;
	fetchSymbolCategory: (id: number) => Promise<void>;
	fetchAllSymbols: () => Promise<void>;
}

const SymbolsContext = createContext<SymbolsContextType | undefined>(undefined);

interface SymbolsProviderProps {
	children: ReactNode;
}

export const SymbolsProvider: React.FC<SymbolsProviderProps> = ({ children }) => {
	const {t} = useTranslation();
	const [symbolCategories, setSymbolCategories] = useState<SymbolCategoryDTO[]>([]);
	const [allSymbolsData, setAllSymbolsData] = useState<SymbolCategoryDTO[]>([]);
	const [selectedCategoryData, setSelectedCategoryData] = useState<SymbolCategoryDTO | null>(null);
	const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(false);
	const [isLoadingSymbols, setIsLoadingSymbols] = useState<boolean>(false);
	const [isLoadingAllSymbols, setIsLoadingAllSymbols] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// Fetch symbol categories and all symbols on mount
	useEffect(() => {
		const fetchCategoriesAndSymbols = async () => {
			setIsLoadingCategories(true);
			setIsLoadingAllSymbols(true);
			setError(null);
			try {
				// First fetch categories
				const categories = await symbolsAPI.getSymbolCategories();
				setSymbolCategories(categories);

				const allCategoriesWithSymbols = await Promise.all(
					categories.map(async (category) => {
						const categoryData = await symbolsAPI.getSymbolCategory(category.id);
						return categoryData;
					})
				);
				setAllSymbolsData(allCategoriesWithSymbols);
			} catch (error) {
				setError(t('symbols.failed_to_load_symbols_for_category'));
				console.error('Failed to load symbol categories or symbols:', error);
			} finally {
				setIsLoadingCategories(false);
				setIsLoadingAllSymbols(false);
			}
		};

		fetchCategoriesAndSymbols();
	}, [t]);

	const fetchSymbolCategory = useCallback(async (id: number) => {
		setIsLoadingSymbols(true);
		setError(null);
		try {
			const categoryData = await symbolsAPI.getSymbolCategory(id);
			setSelectedCategoryData(categoryData);
		} catch (error) {
			setError(t('symbols.failed_to_load_symbols_for_category'));
			console.error(`Failed to load symbols for category ${id}:`, error);
			setSelectedCategoryData(null);
		} finally {
			setIsLoadingSymbols(false);
		}
	}, [t]);
	const fetchAllSymbols = useCallback(async () => {
		// If already loaded, return early
		if (allSymbolsData.length > 0) {
			return;
		}

		// If categories aren't loaded yet, wait for the initial load
		if (symbolCategories.length === 0) {
			return;
		}

		setIsLoadingAllSymbols(true);
		setError(null);
		try {
			const allCategoriesWithSymbols = await Promise.all(
				symbolCategories.map(async (category) => {
					const categoryData = await symbolsAPI.getSymbolCategory(category.id);
					return categoryData;
				})
			);
			setAllSymbolsData(allCategoriesWithSymbols);
		} catch (error) {
			setError(t('symbols.failed_to_load_all_symbols'));
			console.error('Failed to load all symbols:', error);
		} finally {
			setIsLoadingAllSymbols(false);
		}
	}, [allSymbolsData.length, symbolCategories, t]);

	const value = {
		symbolCategories,
		allSymbolsData,
		selectedCategoryData,
		isLoadingCategories,
		isLoadingSymbols,
		isLoadingAllSymbols,
		error,
		fetchSymbolCategory,
		fetchAllSymbols
	};

	return (
		<SymbolsContext.Provider value={value}>
			{children}
		</SymbolsContext.Provider>
	);
};

export const useSymbolsContext = (): SymbolsContextType => {
	const context = useContext(SymbolsContext);
	if (context === undefined) {
		throw new Error('useSymbols must be used within a SymbolsProvider');
	}
	return context;
};

export default SymbolsContext;
